import { Button } from "@rhema/ui";
import { useState } from "react";
import { useApi } from "../session/session";
import { setPrefs, useNotificationPrefs } from "./prefs";
import { browserPushEnv, enablePush, type PushEnv } from "./push";

export function resultMessage(result: string): string | null {
  if (result === "denied")
    return "Notifications are blocked for this site. Allow them in your browser's site settings, then try again.";
  if (result === "unsupported") return "This browser cannot show notifications.";
  if (result === "unavailable")
    return "Notifications show while Rhema is open in a tab. This server does not send push notifications yet.";
  return null;
}

// A card above the chat list that offers notifications. It never asks the browser on its own,
// only when the person taps the button.
export function NotificationPrompt({ env = browserPushEnv() }: { env?: PushEnv }) {
  const api = useApi();
  const prefs = useNotificationPrefs();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (message) {
    return (
      <p
        role="status"
        className="m-0 mx-5 mb-2 rounded-field bg-surface-header px-3.5 py-2.5 text-[13px] text-ink"
      >
        {message}
      </p>
    );
  }
  if (env.permission() !== "default" || prefs.enabled !== null || prefs.promptDismissed) return null;

  const turnOn = async () => {
    setBusy(true);
    try {
      const result = await enablePush(api, env);
      setMessage(resultMessage(result));
    } catch {
      setMessage("Notifications could not be turned on. Try again from Settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-5 mb-2 rounded-card border border-line bg-surface px-4 py-3">
      <p className="m-0 text-[15px] font-semibold">Get notified about new messages</p>
      <p className="m-0 mt-0.5 text-[13px] text-ink-secondary">
        Notifications say who wrote to you. The message itself is never sent in a notification.
      </p>
      <div className="mt-2.5 flex gap-2">
        <Button size="sm" disabled={busy} onClick={() => void turnOn()}>
          Turn on notifications
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setPrefs({ promptDismissed: true })}>
          Not now
        </Button>
      </div>
    </div>
  );
}
